import React, { useState, useCallback, useMemo } from 'react';
import './calendar-single.style.scss';

interface CalendarSingleProps {
  onDateSelect?: (date: Date) => void;
  label?: string;
  minYear?: number;
  maxYear?: number;
}

const CalendarSingle: React.FC<CalendarSingleProps> = ({
  onDateSelect,
  label = 'Select Date',
  minYear = 1900,
  maxYear,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  // Memoize maxYear to avoid recalculation on every render
  const finalMaxYear = useMemo(() => maxYear ?? new Date().getFullYear(), [maxYear]);

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  }, []);

  const handleYearChange = useCallback((year: number) => {
    setCurrentMonth(prev => new Date(year, prev.getMonth()));
    setShowYearDropdown(false);
  }, []);

  const handleDateClick = useCallback((day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (onDateSelect) {
      onDateSelect(selectedDate);
    }
  }, [currentMonth, onDateSelect]);

  const renderCalendarGrid = useCallback((): JSX.Element => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
    const days: (number | null)[] = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return (
      <div className="calendar-grid">
        <div className="calendar-header-row">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, idx) => (
            <div key={idx} className="calendar-header-cell">
              {day}
            </div>
          ))}
        </div>

        <div className="calendar-body">
          {Array(Math.ceil(days.length / 7))
            .fill(null)
            .map((_, weekIdx) => (
              <div key={weekIdx} className="calendar-row">
                {days.slice(weekIdx * 7, (weekIdx + 1) * 7).map((day, dayIdx) => {
                  if (day === null) {
                    return <div key={`empty-${weekIdx}-${dayIdx}`} className="calendar-cell calendar-cell--disabled" />;
                  }

                  return (
                    <div
                      key={`day-${weekIdx}-${dayIdx}`}
                      className="calendar-cell"
                      onClick={() => handleDateClick(day)}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
    );
  }, [currentMonth, handleDateClick]);

  const formatMonth = useCallback((date: Date): string => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return monthNames[date.getMonth()];
  }, []);

const yearOptions = useMemo(() => {
  return Array.from(
    { length: finalMaxYear - minYear + 1 },
    (_, i) => finalMaxYear - i
  );
}, [minYear, finalMaxYear]);


  const toggleYearDropdown = useCallback(() => {
    setShowYearDropdown(prev => !prev);
  }, []);

  return (
    <div className="calendar-single-wrapper">
      <div className="calendar-single-container">
        <div className="calendar-single-header">
          <div className="calendar-month-selector">
            <h3>{formatMonth(currentMonth)}</h3>
            <div className="calendar-year-selector">
              <button
                type="button"
                className="year-dropdown-btn"
                onClick={() => toggleYearDropdown()}
                aria-label="Select year"
              >
                {currentMonth.getFullYear()}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showYearDropdown && (
                <div className="year-dropdown-menu">
                  <div className="year-dropdown-list">
                    {yearOptions.map((year) => (
                      <button
                        type="button"
                        key={year}
                        className={`year-option ${year === currentMonth.getFullYear() ? 'active' : ''}`}
                        onClick={() => handleYearChange(year)}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="calendar-nav">
            <button type="button" className="nav-btn" onClick={handlePrevMonth} aria-label="Previous month">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 19L8 12L15 5"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button type="button" className="nav-btn" onClick={handleNextMonth} aria-label="Next month">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 5L16 12L9 19"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
        {renderCalendarGrid()}
      </div>
    </div>
  );
};

export default CalendarSingle;
